import { CarModelColorDto } from "api"
import { trackColorSliderModelPageClicks } from "common/analytics"
import { ColorCarouselProps } from "common/mapDataToFitCarousel"
import { STEPS_EN } from "components/shopFeatures/wizard/constants"
import { useCallback, useEffect, useMemo, useState } from "react"
import ColorCircle from "reusable/colors/ColorCircle"
import styled, { css } from "styled-components"
import { A11y, Keyboard, Parallax } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import colors from "theme/colors"
import { TypographyCenter } from "theme/shared"

const StyledWrap = styled.div<{ $pathname: string }>`
  padding: 0;
  width: 100%;
  max-width: 700px;
  margin: 0px auto;
  overflow: ${(p) =>
    p.$pathname === STEPS_EN.color || p.$pathname === "product"
      ? "visible !important"
      : "hidden"};

  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: ${(p) => {
      if (p.$pathname == STEPS_EN.upholstery) return "4px"
      if (p.$pathname == STEPS_EN.color) return "0px"
      return "4px"
    }};

    ${(p) => {
      if (p.$pathname === STEPS_EN.color) {
        return `
      position: relative;
      top: -40px;
    `
      }
    }}
  }
`
const StyledSwiperWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`
const StyledSwiperJs = styled(Swiper).attrs({})`
  height: 100px;
  width: 100%;
  max-width: 560px;

  & .swiper-wrapper {
    transition-timing-function: cubic-bezier(0.47, 0.5, 0.57, 1.19);
  }
  .swiper-slide,
  .swiper-slide-duplicate {
    opacity: 0;
    transition: 0.3s ease all;
    transform: scale(0.65) translate(0, 10px) !important;
    transform-origin: top;
  }
  .swiper-slide-visible,
  .swiper-slide-duplicate-visible {
    opacity: 1;
    ${(p) =>
      p.loop &&
      css`
        &:first-child {
          transform: scale(0.65) translate(10px, 10px) !important;
        }
      `}
  }
  .swiper-slide-active,
  .swiper-slide-duplicate-active {
    /* opacity: 1; */
    transform: scale(1) !important;
  }
  .swiper-slide-next,
  .swiper-slide-duplicate-next {
    transform: scale(0.65) translate(-10px, 10px) !important;
    ${(p) =>
      p.slidesPerView !== "auto" &&
      css`
        transform: scale(0.65) translate(10px, 0px) !important;
      `}
  }
  .swiper-slide-prev,
  .swiper-slide-duplicate-prev {
    transform: scale(0.65) translate(10px, 10px) !important;
    ${(p) =>
      p.slidesPerView !== "auto" &&
      css`
        transform: scale(0.65) translate(-10px, 0px) !important;
      `}
  }
`

const StyledSwiperSlide = styled(SwiperSlide)`
  width: auto;
  box-sizing: border-box;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  position: relative;
`

const StyledCircle = styled(ColorCircle)<{
  $isVisible: boolean
  $colorChange?: boolean
}>`
  opacity: 0;
  opacity: ${(p) => (p.$isVisible ? 1 : 0)};
  transition: 0.3s ease all;
  pointer-events: ${(p) => (!p.$colorChange ? "auto" : "none")};
`
const StyledNotInStock = styled.div<{ $active: boolean }>`
  font-size: ${(p) => (p.$active ? "10px" : "16px")};
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: ${(p) => p.theme.colors.mazdaRed};
  text-align: center;
  position: absolute;
  top: 100%;
  width: 100%;
  left: 0;
  ${(p) =>
    p.$active &&
    css`
      position: relative;
      top: 0;
    `};
`

const StyledTextWrapper = styled.div<{
  $position: "top" | "bottom"
  $pathname?: string
}>`
  display: flex;
  flex-direction: column;

  align-items: center;
  height: 45px;
  ${(p) =>
    p.$position === "top" &&
    css`
      position: absolute;
      bottom: 100%;
      left: 0;
      width: 100%;
      text-shadow: 0px 4px 10px rgb(0 0 0 / 10%);
    `}

  ${(p) => p.theme.breakpoints.down("sm")} {
    ${(p) =>
      p.$pathname === STEPS_EN.upholstery && "justify-content: flex-end;"}
  }
`

type ColorsCarouselProps = {
  colorsData:
    | ColorCarouselProps[]
    | (CarModelColorDto & { inStock?: boolean })[]
  handleColorChange?: (index: number) => void
  selectedColorIndex?: number
  loop?: boolean
  textPosition?: "top" | "bottom"
  colorChange?: boolean
  showExtraPrice?: boolean
  pathname?: string
  showStock?: boolean
}

//
// THIS IS A SHARED COMPONENT BETWEEN CAR MODEL PAGE AND SHOP FEATURES WIZARD. PLACE CHANGES ACCORDINGLY.
// and make sure it is not breaking the other component.
//

const ColorsCarousel = ({
  colorsData,
  selectedColorIndex,
  handleColorChange,
  loop = true,
  textPosition = "bottom",
  colorChange,
  showExtraPrice = false,
  pathname = "product",
  showStock = true,
}: ColorsCarouselProps) => {
  const [selectedColorNumber, setSelectedColorNumber] = useState(
    selectedColorIndex || 0
  )
  const [mounted, setMounted] = useState(false)
  const VISUAL_SLIDES = 7

  // loop Array, concat colorsData array as much times that is needed based on VISUAL_SLIDES const value. the array length needs to be at least double the VISUAL_SLIDES const value.
  const loopArray = useMemo(() => {
    if (!loop) return colorsData
    const originalArray = colorsData
    let duplicatedArray = [...originalArray]
    try {
      while (duplicatedArray.length < VISUAL_SLIDES * 2) {
        duplicatedArray = [...duplicatedArray, ...originalArray]
      }
    } catch (error) {
      console.log(error)
    }

    return duplicatedArray
  }, [colorsData, loop])

  useEffect(() => {
    if (window) setMounted(true)
  }, [])

  const getAdditionalPriceContent = useCallback(() => {
    const color = colorsData[selectedColorNumber] as ColorCarouselProps
    if (color?.additionalPrice && showExtraPrice)
      return `תוספת מחיר - ${color?.additionalPrice}`

    if (color?.additionalPrice || color?.hasAdditionalPrice)
      return "בתוספת מחיר"
  }, [colorsData, selectedColorNumber, showExtraPrice])

  if (!mounted) return null

  return (
    <StyledWrap $pathname={pathname}>
      <StyledSwiperWrapper>
        <StyledSwiperJs
          modules={[A11y, Keyboard, Parallax]}
          loop={loop}
          parallax
          slidesPerView={pathname === STEPS_EN.upholstery ? 3 : "auto"}
          allowTouchMove
          grabCursor
          slideToClickedSlide
          centeredSlides
          resizeObserver
          initialSlide={selectedColorIndex || 0}
          keyboard={{
            enabled: true,
          }}
          onSlideChange={(swiper) => {
            // get colorIndex based on that the original array was LOOP_REPEAT the length
            const colorIndex = swiper.realIndex % colorsData.length

            handleColorChange && handleColorChange(colorIndex)
            setSelectedColorNumber(colorIndex)
          }}
        >
          {loopArray.map((color, index) => {
            return (
              <StyledSwiperSlide
                key={`swiperr-${index}`}
                id={`swiperr-${index}`}
              >
                {({ isVisible, isActive }) => {
                  return (
                    <>
                      <StyledCircle
                        $isVisible={isVisible}
                        hex={color?.hex || ""}
                        imageUrl={color?.colorImage?.imageUrl}
                        height={80}
                        $colorChange={colorChange}
                        additionalPrice={color?.hasAdditionalPrice}
                        onClick={() => trackColorSliderModelPageClicks()}
                      />
                      {showStock && !color.inStock && !isActive && (
                        <StyledNotInStock $active={false}>
                          לא במלאי
                        </StyledNotInStock>
                      )}
                    </>
                  )
                }}
              </StyledSwiperSlide>
            )
          })}
        </StyledSwiperJs>
      </StyledSwiperWrapper>
      <StyledTextWrapper $position={textPosition} $pathname={pathname}>
        <TypographyCenter
          $fontSize="16px"
          variant="body2"
          $color={textPosition === "top" ? colors.white : colors.black}
        >
          {colorsData[selectedColorNumber]?.name}
        </TypographyCenter>
        <TypographyCenter
          variant="caption"
          $color={textPosition === "top" ? colors.white : colors.disable}
        >
          {getAdditionalPriceContent()}
        </TypographyCenter>
        {showStock && !colorsData[selectedColorNumber]?.inStock && (
          <StyledNotInStock $active={true}>לא במלאי</StyledNotInStock>
        )}
      </StyledTextWrapper>
    </StyledWrap>
  )
}

export default ColorsCarousel
